
import axios, { AxiosError } from 'axios';

/**
 * Determines if an error is a "logical" server error vs a network/offline error.
 * * @param error - The caught error object
 * @returns {boolean} true if it's a logical error, false if it's a network/offline issue
 */
export function isLogicalError(error: unknown): boolean {
  // 1. If it's not an Axios error at all (e.g., a TypeError in your code),
  // we treat it as a logical bug, not an offline state.
  if (!axios.isAxiosError(error)) {
    return true;
  }

  const axiosError = error as AxiosError;

  // 2. Check for an explicit server response.
  // If `error.response` exists, the request reached the server and we got an HTTP status code.
  if (axiosError.response) {
    // Optional refinement: If you want 5xx (Server Crash) errors to ALSO trigger
    // the offline cache, you can uncomment the following lines:
    // const status = axiosError.response.status;
    // if (status >= 500) return false;

    return true; // e.g., 400, 401, 403, 404
  }

  // 3. Check for specific Axios network error codes.
  // ERR_NETWORK: Client is offline, CORS blocked, or server is unreachable.
  // ECONNABORTED: Request timed out.
  // ETIMEDOUT: Node.js specific timeout error.
  const offlineErrorCodes = ['ERR_NETWORK', 'ECONNABORTED', 'ETIMEDOUT'];
  if (axiosError.code && offlineErrorCodes.includes(axiosError.code)) {
    return false;
  }

  // 4. Fallback check for no response.
  // `error.request` exists but `error.response` does not.
  // This is a classic indicator that the network request failed mid-flight.
  if (axiosError.request) {
    return false;
  }

  // 5. If we get here, it's likely an error setting up the request itself.
  // Treat as logical so we don't accidentally hide bad request configurations in the cache.
  return true;
}
